/**
 * Cookie consent for the logged-out public Publish surface (`/p`).
 *
 * Public pages are anonymous entry points, so analytics must wait for the
 * visitor's CookieYes choice. This uses the same CookieYes site as
 * buffer.com, so a choice made on marketing carries over when the consent
 * cookie is shared across `.buffer.com`.
 *
 * Only `/p` paths are affected: everywhere else `whenAnalyticsConsented` runs
 * its callback immediately and CookieYes is never loaded, so the
 * authenticated app keeps its current behavior.
 *
 * If CookieYes fails to load (blocked, offline), analytics stays off on `/p`.
 */
import {
  COOKIEYES_BANNER_STYLE,
  keepCookieBannerUsableOverModals,
} from './publicCookieBannerAccess'
import { isPublicPublishPath } from './publicPublishPaths'

export const COOKIEYES_SCRIPT_ID = 'cookieyes'
export const COOKIEYES_SCRIPT_SRC =
  'https://cdn-cookieyes.com/client_data/73a7491b50fa6e35a4e9e152/script.js'
export const COOKIEYES_STYLE_ID = 'cookieyes-publish-overrides'

// The CookieYes site (shared with buffer.com) places the revisit-consent
// button bottom-left. Publish moves it to the bottom-right without changing
// buffer.com; the doubled class outranks CookieYes' own rule.
const COOKIEYES_STYLE = `
.cky-btn-revisit-wrapper.cky-revisit-bottom-left {
  left: auto;
  right: 15px;
}
`

type CookieYesCategory =
  | 'necessary'
  | 'functional'
  | 'analytics'
  | 'performance'
  | 'advertisement'

/** Shape returned by `getCkyConsent()` and sent with `cookieyes_banner_load`. */
export type CookieYesConsent = {
  activeLaw: string
  categories: Record<CookieYesCategory, boolean>
  isUserActionCompleted: boolean
  consentID: string
  languageCode: string
}

/** Sent with `cookieyes_consent_update` after the visitor makes a choice. */
type CookieYesConsentUpdate = {
  accepted: CookieYesCategory[]
  rejected: CookieYesCategory[]
}

export function requiresCookieConsent(
  pathname: string = window.location.pathname,
): boolean {
  return isPublicPublishPath(pathname)
}

export function isAnalyticsConsented(): boolean {
  return !requiresCookieConsent() || hasAnalyticsConsent
}

/** Injects the CookieYes banner script and its style overrides on public paths only. */
export function loadCookieYes(): void {
  if (!requiresCookieConsent()) return
  if (document.getElementById(COOKIEYES_SCRIPT_ID)) return

  const style = document.createElement('style')
  style.id = COOKIEYES_STYLE_ID
  style.textContent = COOKIEYES_STYLE + COOKIEYES_BANNER_STYLE
  document.head.appendChild(style)

  const script = document.createElement('script')
  script.id = COOKIEYES_SCRIPT_ID
  script.src = COOKIEYES_SCRIPT_SRC
  document.head.appendChild(script)

  keepCookieBannerUsableOverModals()
}

/** Indirection so tests can observe the reload; jsdom can't stub `location`. */
export const pageLifecycle = {
  reload: (): void => window.location.reload(),
}

function onAnalyticsWithdrawal(event: Event): void {
  const detail = (event as CustomEvent<CookieYesConsentUpdate | undefined>)
    .detail
  if (hasAnalyticsConsent && detail?.rejected?.includes('analytics')) {
    hasAnalyticsConsent = false
    pageLifecycle.reload()
  }
}

let hasAnalyticsConsent = false
let isWatchingForWithdrawal = false
const pendingConsentCleanups = new Set<() => void>()

/**
 * Loaded analytics libraries can't be switched off in place, so when a
 * visitor withdraws analytics consent (via the CookieYes revisit button)
 * reload the page; on the next load the gate sees no consent and stays shut.
 * Shared by every gated script so one withdrawal triggers one reload.
 */
function reloadWhenAnalyticsWithdrawn(): void {
  if (isWatchingForWithdrawal) return
  isWatchingForWithdrawal = true

  document.addEventListener('cookieyes_consent_update', onAnalyticsWithdrawal)
}

/** Test-only: remove consent listeners and reset permission between tests. */
export function resetConsentWatchersForTest(): void {
  for (const cleanup of pendingConsentCleanups) cleanup()
  document.removeEventListener(
    'cookieyes_consent_update',
    onAnalyticsWithdrawal,
  )
  hasAnalyticsConsent = false
  isWatchingForWithdrawal = false
}

/**
 * Runs `onConsent` once analytics may load: immediately outside `/p`, and on
 * `/p` once CookieYes reports the analytics category as allowed (a stored
 * choice, a region default, or the visitor accepting the banner). If the
 * visitor later withdraws analytics consent, the page reloads.
 */
export function whenAnalyticsConsented(onConsent: () => void): void {
  if (!requiresCookieConsent()) {
    onConsent()
    return
  }

  let hasRun = false

  const run = (): void => {
    if (hasRun) return
    hasRun = true
    hasAnalyticsConsent = true
    cleanup()
    reloadWhenAnalyticsWithdrawn()
    onConsent()
  }

  function onBannerLoad(event: Event): void {
    const detail = (event as CustomEvent<CookieYesConsent | undefined>).detail
    if (detail?.categories?.analytics === true) run()
  }

  function onConsentUpdate(event: Event): void {
    const detail = (event as CustomEvent<CookieYesConsentUpdate | undefined>)
      .detail
    if (detail?.accepted?.includes('analytics')) run()
  }

  function cleanup(): void {
    document.removeEventListener('cookieyes_banner_load', onBannerLoad)
    document.removeEventListener('cookieyes_consent_update', onConsentUpdate)
    pendingConsentCleanups.delete(cleanup)
  }

  pendingConsentCleanups.add(cleanup)
  document.addEventListener('cookieyes_banner_load', onBannerLoad)
  document.addEventListener('cookieyes_consent_update', onConsentUpdate)

  // CookieYes may already be ready if another script loaded it first.
  if (window.getCkyConsent?.().categories?.analytics === true) run()
}
